"use client";
import * as React from "react";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { sectionWrapper } from "./sectionWrappers";
import {
  SectionBackgroundColorType,
  SectionTopPaddingSizeType,
  SectionBottomPaddingSizeType,
} from "./types";
import { cn } from "@repo/utils/cn";
import { DatePicker, DatePickerValue } from "./DatePicker";
import { buildReservationUrl } from "./utils/dateTime";
import { IMAGE_QUALITY } from "./constants";

export interface LocationCardAmenity {
  title?: string;
  icon?: {
    src: string;
    alt?: string;
  };
}

export interface LocationCardProps
  extends Omit<ComponentProps<"section">, "children" | "id"> {
  title?: string;
  address?: string;
  description?: React.ReactNode;
  locationImage?: {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
  } | null;
  amenitiesTitle?: string;
  amenities?: LocationCardAmenity[];
  backgroundColor?: SectionBackgroundColorType;
  topPadding?: SectionTopPaddingSizeType;
  bottomPadding?: SectionBottomPaddingSizeType;
  id?: string;
  datePlaceholder?: string;
  dateFormat?: string;
  minDate?: Date;
  maxDate?: Date;
  onDateChange?: (value: DatePickerValue) => void;
  checkAvailabilityButtonText?: string;
  onCheckAvailability?: () => void;
  locationId?: string;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  title,
  address,
  description,
  locationImage,
  amenitiesTitle,
  amenities,
  backgroundColor = "none",
  topPadding = "default",
  bottomPadding = "default",
  id,
  className,
  datePlaceholder = "Select date",
  dateFormat = "MM/dd/yyyy",
  minDate,
  maxDate,
  onDateChange,
  checkAvailabilityButtonText = "Check availability",
  onCheckAvailability,
  locationId,
  ...props
}) => {
  const [datePickerValue, setDatePickerValue] = React.useState<DatePickerValue>(
    {
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  );
  const [dateError, setDateError] = React.useState(false);

  const handleDateChange = (value: DatePickerValue) => {
    setDatePickerValue(value);
    onDateChange?.(value);
    if (dateError) setDateError(false);
  };

  const handleCheckAvailability = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!datePickerValue.startDate) {
      e.preventDefault();
      setDateError(true);
      return;
    }
    onCheckAvailability?.();
  };

  return (
    <section
      id={id}
      className={sectionWrapper({
        backgroundColor,
        topPadding,
        bottomPadding,
        pageType: "location",
      })}
      {...props}
    >
      <div
        className={cn(
          "uipm:grid",
          "uipm:gap-[var(--container-gutter)]",
          "uipm:px-[var(--container-margin)] uipm:lg:px-0",
          "uipm:grid-cols-1",
          "uipm:lg:grid-cols-[330px_1fr]",
        )}
      >
        {(title || address) && (
          <div
            className={cn(
              "uipm:flex uipm:flex-col",
              "uipm:gap-[var(--container-gutter)] uipm:lg:gap-[var(--margin-margin-xs)]",
              "uipm:pb-[var(--container-card-padding)]",
              "uipm:col-start-1",
              "uipm:lg:col-start-2",
              "uipm:row-start-1",
            )}
          >
            {title && (
              <h1
                className={cn(
                  "uipm:font-normal",
                  "uipm:text-(length:--typography-sizes-headline-4)",
                  "uipm:leading-[var(--typography-line-heights-headline-4)]",
                  "uipm:text-[var(--typography-colors-text-heading)]",
                )}
              >
                {title}
              </h1>
            )}
            {address && (
              <p
                className={cn(
                  "uipm:text-[var(--color-color-grey)]",
                  "uipm:text-(length:var(--typography-sizes-body-medium))",
                  "uipm:font-medium",
                  "uipm:leading-[var(--typography-line-heights-body-medium)]",
                )}
              >
                {address}
              </p>
            )}
          </div>
        )}

        {locationImage && (
          <div
            className={cn(
              "uipm:flex uipm:flex-col",
              "uipm:gap-[var(--container-gutter)] uipm:lg:gap-4",
              "uipm:col-start-1",
              "uipm:row-start-2 uipm:lg:row-start-1",
              "uipm:lg:row-span-2",
              "uipm:lg:pt-[var(--margin-margin-sm)]",
              "uipm:lg:pr-[var(--container-gutter)]",
            )}
          >
            <div className="uipm:relative uipm:w-full uipm:aspect-[330/220]">
              <Image
                src={locationImage.src}
                alt={
                  parseAiAlt(locationImage.alt).cleanAlt || title || "Location"
                }
                fill
                sizes="(max-width: 1024px) 100vw, 330px"
                className="uipm:rounded-[var(--radius-radius-sm)] uipm:object-cover"
              />
              <AiDisclaimerForAlt
                alt={locationImage.alt}
                src={locationImage.src}
              />
            </div>
            <div
              className={cn(
                "uipm:w-full",
                "uipm:p-[var(--container-margin)] uipm:lg:p-[var(--padding-padding-sm)]",
                "uipm:bg-[rgba(130,130,130,0.1)]",
                "uipm:rounded-[var(--radius-radius-sm)]",
                "uipm:flex uipm:flex-col",
                "uipm:gap-[var(--container-margin)] uipm:lg:gap-[var(--margin-margin-sm)]",
              )}
            >
              <DatePicker
                selectsRange
                showTimeSelect
                onChange={handleDateChange}
                dateFormat={dateFormat}
                minDate={minDate}
                maxDate={maxDate}
                placeholderText={datePlaceholder}
                state={dateError ? "error" : "default"}
                background="white"
                containerClassName="uipm:w-full"
              />
              <a
                href={buildReservationUrl(locationId, datePickerValue)}
                onClick={handleCheckAvailability}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "uipm:w-full",
                  "uipm:px-[var(--padding-padding-md)]",
                  "uipm:py-[var(--padding-padding-sm-plus)]",
                  "uipm:bg-[var(--surface-surface-accent)]",
                  "uipm:text-[var(--typography-colors-text-inverted)]",
                  "uipm:rounded-[var(--radius-radius-sm)]",
                  "uipm:border-0",
                  "uipm:font-semibold",
                  "uipm:cursor-pointer",
                  "uipm:text-(length:--typography-sizes-body-medium)",
                  "uipm:leading-[var(--typography-line-heights-body-medium)]",
                  "hover:uipm:opacity-90",
                  "uipm:transition-opacity",
                  "uipm:text-center",
                  "uipm:no-underline",
                  "uipm:inline-block",
                )}
              >
                {checkAvailabilityButtonText}
              </a>
            </div>
          </div>
        )}

        <div
          className={cn(
            "uipm:flex uipm:flex-col",
            "uipm:gap-[var(--container-card-padding)]",
            "uipm:col-start-1",
            "uipm:row-start-3",
            "uipm:lg:col-start-2",
            "uipm:lg:row-start-2",
          )}
        >
          {description && <div>{description}</div>}

          {(amenitiesTitle || amenities) && (
            <div className="uipm:flex uipm:flex-col uipm:gap-[var(--padding-padding-sm)]">
              {amenitiesTitle && (
                <h4
                  className={cn(
                    "uipm:text-[var(--typography-colors-text-faded)]",
                    "uipm:text-(length:var(--typography-sizes-body-large))",
                    "uipm:font-medium",
                    "uipm:leading-[var(--typography-line-heights-body-large)]",
                  )}
                >
                  {amenitiesTitle}
                </h4>
              )}
              {amenities && amenities.length > 0 && (
                <div
                  className={cn(
                    "uipm:py-[var(--padding-padding-m)]",
                    "uipm:px-[var(--container-gutter)]",
                    "uipm:grid",
                    "uipm:grid-cols-[repeat(auto-fit,minmax(100px,1fr))]",
                    "uipm:gap-y-6",
                    "uipm:border-t uipm:border-b",
                    "uipm:border-[rgba(56,114,78,0.20)]",
                    "uipm:bg-[rgba(73,155,69,0.05)]",
                  )}
                >
                  {amenities.map((amenity, index) => (
                    <div
                      key={index}
                      className={cn(
                        "uipm:flex uipm:flex-col",
                        "uipm:gap-[var(--padding-padding-xs)]",
                        "uipm:px-[var(--padding-padding-sm-plus)]",
                        "uipm:items-center uipm:text-center",
                        "uipm:min-w-[100px]",
                      )}
                    >
                      {amenity.icon && (
                        <Image
                          src={amenity.icon.src}
                          alt={amenity.icon.alt || amenity.title || ""}
                          width={32}
                          height={32}
                          quality={IMAGE_QUALITY}
                          className="uipm:w-8 uipm:h-8"
                        />
                      )}
                      {amenity.title && (
                        <p
                          className={cn(
                            "uipm:text-(length:--typography-sizes-body-small)",
                            "uipm:font-normal",
                            "uipm:leading-[var(--typography-line-heights-body-small)]",
                            "uipm:text-[var(--typography-colors-label-inverted)]",
                          )}
                        >
                          {amenity.title}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
